import { Button } from "@/components/button/Button";
import { useDialog } from "@/components/dialog/DialogContext";
import TableTab from "@/components/tabs/table-tab";
import { DEF_PG_SIZE_SUB_MIN } from "@/constants/maximum-page-size";
import { useLocale } from "@/contexts/localeContext";
import { TableContextProvider } from "@/contexts/table/TableContextProvider";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { ListData } from "@/models/ListData";
import { ListQuery } from "@/models/ListQuery";
import { MasterExpiry } from "@/models/MasterExpiry";
import { PaginationModel } from "@/models/Pagination";
import { assignUnassignMasterExpiryFromExpiryReason, editMasterExpiry, getMasterExpiryById } from "@/services/expiryReasonService";
import { getErrorMessage, showLoadItemError } from "@/services/utilities/ServiceError";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getEnrolments, getMastersForExpiryReason } from "@/services/enrolmentService";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";
import { extendColumnDefinition, extendLookups, extendSearchColumn, hideColumns, hideLookups } from "@/models/utilities/columnHelper";
import EnrolmentColumns from "@/models/ColumnDefinitions/EnrolmentColumns";
import { ExpiryReason } from "@/models/ExpiryReason";
import MasterExpiryModal from "./Assign/ExpiryReasonModal";
import EditMasterExpiryModal from "./Assign/EditMasterExpiryModal";
import { Lookup } from "@/models/Lookup";
import LookupTypes from "@/models/LookupTypes";
import { getSiteNamesWithPagination } from "@/services/siteService";
import SiteSearch from "@/models/search/SiteSearch";
import { getCompanyNamesWithPagination } from "@/services/companyService";
import CompanySearch from "@/models/search/CompanySearch";
import { getDepartmentNamesWithPagination } from "@/services/departmentService";
import DepartmentSearch from "@/models/search/DepartmentSearch";
import { getCategoryNameWithPagination } from "@/services/categoryService";
import CategorySearch from "@/models/search/CategorySearch";

interface ExpiryReasonsMembersTabProps {
    currentItem: ExpiryReason
    itemId: string;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>
    dataReloadMethod?: () => void;
}

const ExpiryReasonsMembersTab: FC<ExpiryReasonsMembersTabProps> = ({ itemId, setBusy, currentItem, dataReloadMethod }) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const [showManageModal, setShowManageModal] = useState<boolean>(false);
    const [showEditMasterExpiry, setShowEditMasterExpiry] = useState<boolean>(false);
    const [masterExpiry, setMasterExpiry] = useState<MasterExpiry | undefined>(undefined);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const onEdit = async (id: string) => {
        try {
            const masterExpiry: MasterExpiry = await getMasterExpiryById(id);
            setMasterExpiry(masterExpiry);
            setShowEditMasterExpiry(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowEditMasterExpiry(false) });
        }
    }

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>) => {
        try {
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];
            do {
                const result = await getMastersForExpiryReason(translate, itemId, { pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(result.rows.map((row: any) => row.masterId.toString()));
                rows = rows.concat(result.rows);
                pagination = result.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const onMasterExpiryEdit = async (values: MasterExpiry) => {
        setBusy(true);
        try {
            await editMasterExpiry(values);
            toast.success(translate.byKey(TranslationKeys.success), {toastId: "toastMessage"});
            setShowEditMasterExpiry(false);
            dataRefresh();
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    const handleSubmit = async (toBeAssigned: string[], toBeAssignedRows: any[], toBeUnassigned: string[], values: any) => {
        setBusy(true);
        try {
            await assignUnassignMasterExpiryFromExpiryReason(toBeAssigned, toBeUnassigned, currentItem, values);
            toast.success(translate.byKey(TranslationKeys.success));
            setShowManageModal(false);
            dataRefresh();
            dataReloadMethod?.();
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    const getDataMethod = (listParameters?: ListQuery) => {
        return getMastersForExpiryReason(translate, itemId, listParameters)
    }

    useEffect(() =>{
        dataRefresh(getDataMethod);
    },[itemId])

    return <>
        <TableContextProvider
            dataMethod={(listParameters?: ListQuery) =>getDataMethod(listParameters)}
            manualDataRefresh={manualDataRefresh}
            tableDescriptor={hideLookups(hideColumns(extendColumnDefinition(EnrolmentColumns, 'master'), ['building', 'site']), ["name"])}
            searchDescriptor={extendSearchColumn(EnrolmentSearch, "master")}
            useTable
            usePagination
            useSort
            useSearch
            useFilter
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            editMethod={onEdit}
            lookup={extendLookups(lookup, 'master')}
            canExportReport={false} >
            <TableTab
                title={translate.byKey(TranslationKeys.assignedMembers)}
                selectRows={false}
                cyName="AssignedMembers">
                <Button variant="submit" shape="rounded" id="manageButton" className="py-1 px-5" onClick={() => setShowManageModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </Button>
            </TableTab>
        </TableContextProvider>
        <MasterExpiryModal
            assigningType="MEMBERS"
            dataMethod={(listParameters?: ListQuery) => getEnrolments(translate, listParameters)}
            modalTitle={translate.byKey(TranslationKeys.assignMembers)}
            assigningTitle={translate.byKey(TranslationKeys.members)}
            tableColumns={hideLookups(hideColumns(EnrolmentColumns, ['building', 'site']), ["name"])}
            searchColumns={EnrolmentSearch}
            isOpen={showManageModal}
            onClose={() => setShowManageModal(false)}
            getAssignedItems={getAssignedItems}
            handleSubmit={handleSubmit} 
            idPath="masterId"/>
        <EditMasterExpiryModal
            assigningType="MEMBERS"
            isOpen={showEditMasterExpiry}
            onClose={() => setShowEditMasterExpiry(false)}
            masterExpiry={masterExpiry}
            onSave={onMasterExpiryEdit} />
    </>
}

export default ExpiryReasonsMembersTab;
